import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  AppShell,
  Burger,
  Group,
  Text,
  UnstyledButton,
  ActionIcon,
  rem,
  Tooltip,
  Center,
  Stack,
  Drawer,
  Divider,
  Box,
  Avatar,
  Menu,
  Indicator,
  ScrollArea,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconCalculator,
  IconBook,
  IconChartBar,
  IconUser,
  IconLogout,
  IconFileText,
  IconChevronLeft,
  IconChevronRight,
  IconGitBranch,
  IconTrendingUp,
  IconGift,
  IconBell,
  IconSettings,
  IconChevronDown,
  IconTable,
  // IconVideo, // VideoWorker приховано
} from "@tabler/icons-react";

import SignIn from "./components/login/SignIn";
import useAuthStore from "./store/useAuthStore";
import Roi from "./components/roi/Roi";
import Tasker from "./components/tasker/Tasker";
import EditTask from "./components/tasker/EditTask";
import CreateTask from "./components/tasker/CreateTask";
import Report from "./components/report/Report";
import Profile from "./components/profile/Profile";
import Offers from "./components/offers/Offers";
import DocGenerator from "./components/docgen/DocGenerator";
import Workflow from "./components/workflow/Workflow";
import Analyzer from "./components/analyzer/Analyzer";
import Transactions from "./components/transactions/Transactions";
// import VideoWorker from "./components/videoworker/VideoWorker"; // приховано
import useNotificationStore from "./store/useNotificationStore";
import socketService from "./services/socketService";

const navLinks = [
  {
    link: "/roiCalculate",
    label: "ROI Calculator",
    icon: IconCalculator,
    section: "TOOLS",
  },
  {
    link: "/workflow",
    label: "Workflow",
    icon: IconGitBranch,
    section: "TOOLS",
  },
  {
    link: "/analyzer",
    label: "Analyzer",
    icon: IconTrendingUp,
    section: "TOOLS",
  },
  { link: "/tasker", label: "Tasker", icon: IconBook, section: "TOOLS" },
  {
    link: "/report",
    label: "Leads Report",
    icon: IconChartBar,
    section: "TOOLS",
  },
  {
    link: "/docgen",
    label: "Doc Generator",
    icon: IconFileText,
    section: "TOOLS",
  },
  { link: "/offers", label: "Offers", icon: IconGift, section: "TOOLS" },
  {
    link: "/transactions",
    label: "Transactions",
    icon: IconTable,
    section: "TOOLS",
  },
  // VideoWorker — приховано (тимчасово не потрібен)
  // {
  //   link: "/videoworker",
  //   label: "VideoWorker",
  //   icon: IconVideo,
  //   section: "TOOLS",
  // },
];

function SectionLabel({ label }) {
  return (
    <Text
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: "#64748b",
        padding: "22px 16px 8px",
        letterSpacing: "0.5px",
        textTransform: "none",
      }}
    >
      {label}
    </Text>
  );
}

function NavbarLink({ icon: Icon, label, active, onClick, collapsed }) {
  const content = (
    <UnstyledButton
      onClick={onClick}
      style={{
        display: "block",
        width: `calc(100% - ${rem(16)})`,
        padding: "7px 10px",
        borderRadius: 5,
        margin: "2px 8px",
        backgroundColor: active ? "#eae9f2" : "transparent",
        color: active ? "#383344" : "#676672",
        transition: "all 150ms ease",
        border: "1px solid transparent",
        paddingLeft: "10px",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = "#eeeef1";
          e.currentTarget.style.color = "#292932";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.color = "#676672";
        }
      }}
    >
      <Group
        gap="sm"
        justify={collapsed ? "center" : "flex-start"}
        wrap="nowrap"
      >
        <Icon
          style={{
            width: rem(16),
            height: rem(16),
            color: active ? "#665bd1" : "#85838e",
            flexShrink: 0,
            transition: "color 150ms ease",
          }}
          stroke={1.5}
        />
        {!collapsed && (
          <Text style={{ fontSize: 12, fontWeight: active ? 600 : 400, transition: "color 150ms ease" }}>
            {label}
          </Text>
        )}
      </Group>
    </UnstyledButton>
  );

  if (collapsed) {
    return (
      <Tooltip
        label={label}
        position="right"
        withArrow
        transitionProps={{ duration: 0 }}
      >
        {content}
      </Tooltip>
    );
  }
  return content;
}

function NavSidebarContent({
  location,
  navigate,
  user,
  isAdmin,
  logout,
  collapsed,
  onNavigate,
}) {
  return (
    <Stack className="workspace-navigation" h="100%" justify="space-between" gap={0} style={{ paddingBottom: 16 }}>
      <div>
        {/* Brand Header */}
        <Box p="md" style={{ borderBottom: "1px solid #e2e8f0" }} mb="sm">
          {collapsed ? (
            <Center>
              <img src="/logo.svg" alt="Logo" style={{ height: 28 }} />
            </Center>
          ) : (
            <Group gap="xs" wrap="nowrap">
              <Center style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: "#eae9f0" }}>
                <img src="/logo.svg" alt="Logo" style={{ height: 20 }} />
              </Center>
              <div>
                <Text fw={700} size="sm" style={{ color: "#292932", lineHeight: 1.1 }}>
                  Utility
                </Text>
                <Text size="10px" c="dimmed" fw={500}>
                  Profit Junkies workspace
                </Text>
              </div>
            </Group>
          )}
        </Box>

        {!collapsed && <SectionLabel label="Workspace" />}
        {navLinks.map((item) => (
          <NavbarLink
            key={item.link}
            icon={item.icon}
            label={item.label}
            active={location.pathname.startsWith(item.link)}
            collapsed={collapsed}
            onClick={() => {
              navigate(item.link);
              onNavigate?.();
            }}
          />
        ))}
      </div>

      {/* User profile card at the bottom */}
      {user?.email && (
        <Box px="sm" style={{ borderTop: "1px solid #e2e8f0", paddingTop: 12 }}>
          <Menu position={collapsed ? "right-bottom" : "top-start"} withArrow shadow="md">
            <Menu.Target>
              <UnstyledButton
                p={4}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  padding: collapsed ? "4px" : "6px 8px",
                  transition: "background-color 150ms ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#eeeef1")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <Group gap="xs" justify={collapsed ? "center" : "flex-start"} wrap="nowrap">
                  <Avatar radius="xl" color="brand" size={collapsed ? "md" : "sm"}>
                    {user.email.charAt(0).toUpperCase()}
                  </Avatar>
                  {!collapsed && (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        size="xs"
                        fw={600}
                        style={{ color: "#292932" }}
                        truncate
                      >
                        {user.email.split("@")[0]}
                      </Text>
                      <Text size="10px" c="dimmed" truncate>
                        {isAdmin ? "Admin" : "Operator"}
                      </Text>
                    </div>
                  )}
                  {!collapsed && <IconChevronDown size={12} color="#85838e" style={{ flexShrink: 0 }} />}
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{user.email}</Menu.Label>
              <Menu.Item
                leftSection={<IconUser size={14} />}
                onClick={() => navigate("/profile")}
              >
                Profile
              </Menu.Item>
              <Menu.Item
                leftSection={<IconLogout size={14} />}
                color="red"
                onClick={logout}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      )}
    </Stack>
  );
}

const AppContent = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const logout = useAuthStore((state) => state.logout);

  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [desktopOpened, setDesktopOpened] = React.useState(() => {
    const saved = localStorage.getItem("sidebar_opened");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const navigate = useNavigate();
  const location = useLocation();

  const toggleDesktop = () => {
    setDesktopOpened((prev) => {
      const next = !prev;
      localStorage.setItem("sidebar_opened", JSON.stringify(next));
      return next;
    });
  };

  const {
    notifications,
    unreadCount,
    fetchNotifications,
    addNotification,
    markAsRead,
    markAllAsRead,
  } = useNotificationStore();

  React.useEffect(() => {
    if (isAuthenticated && user?.id) {
      // Connect and join personal room
      socketService.connect();
      socketService.joinUser(user.id);

      // Fetch history
      fetchNotifications();

      // Listen for new notifications
      const handleNewNotification = (notification) => {
        addNotification(notification);
      };

      socketService.onNotification(handleNewNotification);

      return () => {
        socketService.offNotification(handleNewNotification);
      };
    }
  }, [isAuthenticated, user?.id, addNotification, fetchNotifications]);

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="*" element={<Navigate to="/signin" replace />} />
      </Routes>
    );
  }

  const sidebarProps = {
    location,
    navigate,
    user,
    isAdmin,
    logout,
  };

  const currentRoute = navLinks.find((link) => location.pathname.startsWith(link.link));

  return (
    <>
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size={260}
        position="left"
        withCloseButton={false}
        padding={0}
      >
        <NavSidebarContent
          {...sidebarProps}
          collapsed={false}
          onNavigate={closeDrawer}
        />
      </Drawer>

      <AppShell
        header={{ height: 48, offset: true }}
        navbar={{
          width: desktopOpened ? 224 : 64,
          breakpoint: "sm",
          collapsed: { mobile: true },
        }}
        padding={0}
      >
        {(
          <AppShell.Header
            style={{
              backgroundColor: "#ffffff",

              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Group h="100%" px="lg" justify="space-between" wrap="nowrap">
              <Group gap="sm">
                {isMobile && <Burger opened={drawerOpened} onClick={openDrawer} size="sm" />}
                <Text size="xs" c="dimmed" visibleFrom="sm">Workspace</Text>
                <Text size="xs" c="dimmed" visibleFrom="sm">/</Text>
                <Text fw={500} size="sm" style={{ color: "#41404c" }}>{currentRoute?.label || "Profile"}</Text>
              </Group>
              <Text size="xs" c="dimmed" visibleFrom="sm">Profit Junkies</Text>
            </Group>
          </AppShell.Header>
        )}

        <AppShell.Navbar className="workspace-sidebar" style={{ borderRight: "1px solid #e7e7eb", backgroundColor: "#f7f7f9" }}>
          <NavSidebarContent {...sidebarProps} collapsed={!desktopOpened} />
          {!isMobile && (
            <Center mt="auto" mb={16}>
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={toggleDesktop}
                color="gray"
              >
                {desktopOpened ? (
                  <IconChevronLeft size={16} />
                ) : (
                  <IconChevronRight size={16} />
                )}
              </ActionIcon>
            </Center>
          )}
        </AppShell.Navbar>

        <AppShell.Main className="workspace-main">
          <div className="workspace-page" data-page={location.pathname.split("/")[1] || "roiCalculate"}>
          <Routes>
            <Route path="/" element={<Navigate to="/roiCalculate" replace />} />
            <Route path="/roiCalculate" element={<Roi />} />
            <Route path="/workflow" element={<Workflow />} />
            <Route path="/tasker" element={<Tasker />} />
            <Route path="/tasker/create" element={<CreateTask />} />
            <Route path="/tasker/edit/:id" element={<EditTask />} />
            <Route path="/report" element={<Report />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/offers" element={<Offers />} />
            <Route path="/docgen" element={<DocGenerator />} />
            <Route path="/analyzer" element={<Analyzer />} />
            <Route path="/transactions" element={<Transactions />} />
            {/* VideoWorker — приховано (тимчасово не потрібен) */}
            {/* <Route path="/videoworker" element={<VideoWorker />} /> */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </div>
        </AppShell.Main>
      </AppShell>
    </>
  );
};

const App = () => (
  <Router>
    <AppContent />
  </Router>
);

export default App;
